<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/Facilities/Keys">Facilities</b-breadcrumb-item>
            <b-breadcrumb-item to="/Facilities/Locks">Locks</b-breadcrumb-item>
            <b-breadcrumb-item active>Add</b-breadcrumb-item>
        </b-breadcrumb>

        <form @submit.prevent="onSubmit()">
            <div class="card">
                <div class="bg-dark card-header text-white">Create New Lock</div>
                <div class="card-body">
                    <transition name="fade">
                        <AppNotification type="danger" v-if="$v.newFacilitiesLocks.$invalid && !$v.newFacilitiesLocks.mustBeUnique && isValidating">
                            {{ "Cannot add duplicate locks" }}
                        </AppNotification>
                    </transition>
                    <div class="row">
                        <b-form-group class="col-lg-6 col-md-12" label="Lock for" v-slot="{ lockFor }">
                            <b-form-radio-group
                              v-model="addLockFor"
                              :options="addLockForOptions"
                              :aria-describedby="lockFor"
                            ></b-form-radio-group>
                        </b-form-group>
                    </div>
                    <div v-if="addLockFor === 2" class="row">
                        <div class="col-lg-2 col-md-3 form-group">
                            <label for="totalOtherToAdd">Number of Locks to Add</label>
                            <input  class="form-control"
                                    v-model="totalOtherToAdd" />
                        </div>
                    </div>
                    <div v-if="addLockFor === 2" class="mb-3 text-left">
                        <button @click.prevent="addForLock()" class="btn btn-success d-print-none">
                            <i class="fa fa-plus"></i> Add
                        </button>
                    </div>
                    <AppCardCollapsable v-if="addLockFor !== 2" :display-footer="false" :is-expanded="true" class="mb-3">
                        <template v-slot:header>Select From Existing</template>
                        <template v-slot:body>
                            <div class="row">
                                <div v-if="addLockFor === 1" class="col-lg-6 col-md-12 form-group">
                                    <label for="buildingLookupId">Building</label>
                                    <select class="form-control" v-model="buildingLookupId">
                                        <option :value="null" disabled>--Select Building--</option>
                                        <option :key="possibleBuilding.id" :value="possibleBuilding.id" v-for="possibleBuilding in possibleBuildings">{{possibleBuilding.name}}</option>
                                    </select>
                                </div>
                            </div>
                            <BTable v-if="addLockFor !== 2"
                                    :busy="isSearching"
                                    :fields="possibleBuildingsOrSpacesTableFields"
                                    :items="possibleBuildingsOrSpaces"
                                    :responsive="true"
                                    primary-key="id"
                                    show-empty
                                    striped
                                    table-class="table-align-middle">

                                <template v-slot:cell(id)="row">
                                    <button @click.prevent="addForLock(row)" class="btn btn-success d-print-none">
                                        <i class="fa fa-plus"></i> Add
                                    </button>
                                </template>
                                <template v-slot:cell(spaceType)="row">
                                    {{row.item.spaceType}} {{row.item.spaceIdentity}}
                                </template>
                                <template v-slot:custom-foot>
                                    <b-tr>
                                        <b-td :colspan="possibleBuildingsOrSpacesTableFields.length">
                                            <AppTablePagination
                                                    :page.sync="filterOptions.page"
                                                    :pageSize.sync="filterOptions.pageSize"
                                                    :totalCount.sync="possibleBuildingsOrSpacesTotalCount"
                                                    v-on:update:page="loadNextBuildingsOrSpaces()"
                                                    v-on:update:pageSize="loadNextBuildingsOrSpaces()"
                                            ></AppTablePagination>
                                        </b-td>
                                    </b-tr>
                                </template>
                            </BTable>
                        </template>
                    </AppCardCollapsable>
                    <BTable :fields="tableFields"
                            :items="newFacilitiesLocks"
                            :responsive="true"
                            show-empty
                            striped
                            table-class="table-align-middle">

                        <template v-slot:cell(id)="row">
                            <button @click.prevent="removeLock(row.index)" class="btn btn-danger d-print-none">
                                <i class="fa fa-trash"></i> Remove
                            </button>
                        </template>

                        <template v-slot:cell(lockType)="row">
                            <b-form-group :class="{'is-invalid': $v.newFacilitiesLocks.$each[row.index].lockType.$invalid && isValidating}"
                                          class="col-lg-6 col-md-12" v-slot="{ lockTypeValue }">
                                <b-form-radio-group
                                  v-model="row.item.lockType"
                                  :options="lockTypes"
                                  :aria-describedby="lockTypeValue"
                                  :disabled="row.item.buildingId === null && row.item.buildingSpaceId === null"
                                ></b-form-radio-group>
                                <span class="invalid-feedback" v-if="!$v.newFacilitiesLocks.$each[row.index].lockType.required && isValidating">Field is required</span>
                            </b-form-group>
                        </template>

                        
                        <template v-slot:cell(spaceType)="row">
                            {{row.item.spaceType}} {{row.item.spaceIdentity}}
                        </template>

                        <template v-slot:cell(lockIdentity)="row">
                            <input :class="{'is-invalid': $v.newFacilitiesLocks.$each[row.index].lockIdentity.$invalid && isValidating}"
                                   class="form-control"
                                    maxlength="30"
                                    v-model="newFacilitiesLocks[row.index].lockIdentity"/>
                        </template>
                    </BTable>
                </div>
                <div class="card-footer d-print-none text-right">
                    <button :disabled="isSubmitting" class="btn btn-primary" type="submit">Save</button>
                    <RouterLink :to="{name:'Locks'}" class="btn btn-danger">Back</RouterLink>
                </div>
            </div>
        </form>

    </div>
</template>

<script>
    import {ToastType} from "@/services/models/shared/ToastType";
    import {required, requiredIf} from "vuelidate/lib/validators";
    import AppNotification from "@/components/shared/AppNotification";
    import AppTablePagination from "@/components/shared/AppTablePagination";
    import AppCardCollapsable from "@/components/shared/AppCardCollapsable.vue";
    import FacilitiesLocksService from "@/services/FacilitiesLocksService";
    import BuildingSpaceService from "@/services/BuildingSpaceService";
    import AddressService from "@/services/AddressService"

    export default {
        components: {
            AppNotification,
            AppCardCollapsable,
            AppTablePagination
        },
        data() {
            return {
                possibleBuildingsOrSpaces: [],
                possibleBuildings: [],
                addLockFor: null,
                totalOtherToAdd: null,
                buildingLookupId: null,
                filterOptions: {
                    page: 1,
                    pageSize: 10
                },
                possibleBuildingsOrSpacesTotalCount: 0,
                buildingSpacesFilterOptions: {
                    returnAll: false,
                    page: 1,
                    pageSize: 10,
                    buildingId: null,
                    spaceType: null,
                    parentSpaceType: null,
                    spaceIdentity: null,
                    excludeBuildingSpaceId: null
                },
                buildingsFilterOptions: {
                    page: 1, 
                    pageSize: 10,
                    name: null,
                    code: null,
                    city: null
                },
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:175px'
                    }, 
                    {
                        key: 'lockType',
                        label: 'Lock Type'
                    },
                    {
                        key: 'buildingName',
                        label: 'Building'
                    },
                    {
                        key: 'spaceType',
                        label: 'Room/Suite',
                    },
                    {
                        key: 'lockIdentity',
                        label: 'Notes/Name/Location',
                    }
                ],
                addLockForOptions: [
                    {
                        value: 0,
                        text: 'Buildings'
                    },
                    {
                        value: 1,
                        text: 'Room/Suites'
                    },
                    {
                        value: 2,
                        text: 'Other'
                    }
                ],
                lockTypes: [
                    {
                        value: 0,
                        text: 'Other'
                    },
                    {
                        value: 1,
                        text: 'Entrance/Door'
                    }
                ],
                alertMessage: {
                    type: null,
                    message: null
                },
                isSubmitting: false,
                isValidating: false,
                isSearching: false,
                newFacilitiesLocks: []
            }
        },
        computed: {
            possibleBuildingsOrSpacesTableFields () {
              return this.addLockFor === 0 ? [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:175px'
                    },
                    {
                        key: 'buildingName',
                        label: 'Building'
                    }
                ] : this.addLockFor === 1 ? [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:175px'
                    },
                    {
                        key: 'buildingName',
                        label: 'Building'
                    },
                    {
                        key: 'spaceType',
                        label: 'Room/Suite',
                    }
                ] : [];
            }
        },
        watch: {
            addLockFor(newVal) {
                const vm = this;
                
                this.totalOtherToAdd = null;
                this.buildingLookupId = null;
                this.possibleBuildingsOrSpaces = [];
                this.possibleBuildings = [];
                this.filterOptions = {
                    page: 1,
                    pageSize: 10
                };
                this.possibleBuildingsOrSpacesTotalCount = 0;
                this.buildingSpacesFilterOptions = {
                    returnAll: false,
                    page: 1,
                    pageSize: 10,
                    buildingId: null,
                    spaceType: null,
                    parentSpaceType: null,
                    spaceIdentity: null,
                    excludeBuildingSpaceId: null
                };
                this.buildingsFilterOptions = {
                    page: 1, 
                    pageSize: 10,
                    name: null,
                    code: null,
                    city: null
                };
                this.isSearching = true;
                if (this.addLockFor === 1) {
                  AddressService.getAllBuildings()
                    .then(response => {
                        vm.possibleBuildings = response;
                    })
                    .catch(() => {
                        vm.alertMessage.type = "danger";
                        vm.alertMessage.message = "Unable to load list of buildings";
                    })
                    .finally(() => {
                      this.isSearching = false;
                    })
                } else if (this.addLockFor === 0) {
                  AddressService.getBuildingList(this.buildingsFilterOptions)
                    .then(response => {
                        vm.possibleBuildingsOrSpaces = response.data.map(e => { return {
                            id: e.id,
                            buildingName: e.name,
                            spaceType: null,
                            spaceIdentity: null
                        }});
                        vm.possibleBuildingsOrSpacesTotalCount = response.totalCount;
                    })
                    .catch(() => {
                        vm.alertMessage.type = "danger";
                        vm.alertMessage.message = "Unable to load list of buildings";
                    })
                    .finally(() => {
                      this.isSearching = false;
                    })
                }
            },

            /**
             * @description Watches the buildingLookupId for check. If it is changed it gets the list of building spaces
             */
            buildingLookupId(newVal) {
                if (newVal) {
                  const vm = this;
                  
                  this.possibleBuildingsOrSpaces = [];
                  this.filterOptions = {
                      page: 1,
                      pageSize: 10
                  };
                  this.possibleBuildingsOrSpacesTotalCount = 0;

                  this.isSearching = true;
                  BuildingSpaceService.getBuildingSpaces(this.buildingLookupId, this.buildingSpacesFilterOptions)
                    .then(response => {
                        vm.possibleBuildingsOrSpaces = response.data.map(e => { return {
                            id: e.id,
                            buildingName: e.buildingName,
                            spaceType: e.spaceType,
                            spaceIdentity: e.spaceIdentity
                        }});
                        vm.possibleBuildingsOrSpacesTotalCount = response.totalCount;
                    }).catch(() => {
                        vm.alertMessage.type = "danger";
                        vm.alertMessage.message = "Unable to load list of parent rooms/suites for this building";
                    })
                    .finally(() => {
                      this.isSearching = false;
                    });
                }
            }
        },
        methods: {
            loadNextBuildingsOrSpaces () {
                const vm = this;
                this.isSearching = true;
                if (this.addLockFor === 0) {
                  this.buildingsFilterOptions.page = this.filterOptions.page;
                  this.buildingsFilterOptions.pageSize = this.filterOptions.pageSize;
                  AddressService.getBuildingList(this.buildingsFilterOptions)
                    .then(response => {
                        vm.possibleBuildingsOrSpaces = response.data.map(e => { return {
                            id: e.id,
                            buildingName: e.name,
                            spaceType: null,
                            spaceIdentity: null
                        }});
                        
                        vm.possibleBuildingsOrSpacesTotalCount = response.totalCount;
                    })
                    .catch(() => {
                        vm.alertMessage.type = "danger";
                        vm.alertMessage.message = "Unable to load list of buildings";
                    })
                    .finally(() => {
                      this.isSearching = false;
                    })
                } else if (this.addLockFor === 1) {
                  this.buildingSpacesFilterOptions.page = this.filterOptions.page;
                  this.buildingSpacesFilterOptions.pageSize = this.filterOptions.pageSize;
                  BuildingSpaceService.getBuildingSpaces(this.buildingLookupId, this.buildingSpacesFilterOptions)
                    .then(response => {
                        vm.possibleBuildingsOrSpaces = response.data.map(e => { return {
                            id: e.id,
                            buildingName: e.buildingName,
                            spaceType: e.spaceType,
                            spaceIdentity: e.spaceIdentity
                        }});
                        
                        vm.possibleBuildingsOrSpacesTotalCount = response.totalCount;
                    }).catch(() => {
                        vm.alertMessage.type = "danger";
                        vm.alertMessage.message = "Unable to load list of parent rooms/suites for this building";
                    })
                    .finally(() => {
                      this.isSearching = false;
                    });
                } else {
                  this.isSearching = false;
                }
            },

            /**
             * @description Creates a lock and redirects if successful
             */
            onSubmit() {
                const vm = this;
                vm.isValidating = true;


                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                FacilitiesLocksService.addFacilitiesLocks(this.newFacilitiesLocks)
                    .then(() => {
                      vm.alert(ToastType.Success, "The lock was successfully created");

                      // Redirect to the locks page
                      vm.$router.replace({name: 'Locks'})
                    })
                    .catch((error) => {
                      vm.alert(ToastType.Danger, error.response.data)
                    })
                    .finally(() => {
                      vm.isValidating = false;
                      vm.isSubmitting = false;
                    })
            },

            addForLock (row) {
              if (this.addLockFor === 0) {
                this.newFacilitiesLocks.push({
                  lockType: 1,
                  buildingId: row.item.id,
                  buildingSpaceId: null,
                  buildingName: row.item.buildingName,
                  lockIdentity: null,
                  spaceType: null,
                  spaceIdentity: null,
                  createdByPersonId: this.$store.getters.user.id
                })
              } else if (this.addLockFor === 1) {
                this.newFacilitiesLocks.push({
                  lockType: 1,
                  buildingId: null,
                  buildingSpaceId: row.item.id,
                  buildingName: row.item.buildingName,
                  lockIdentity: null,
                  spaceType: row.item.spaceType,
                  spaceIdentity: row.item.spaceIdentity,
                  createdByPersonId: this.$store.getters.user.id
                })
              } else if (this.addLockFor === 2) {
                for (let i = 0; i < this.totalOtherToAdd; i++) {
                  this.newFacilitiesLocks.push({
                    lockType: 0,
                    buildingId: null,
                    buildingSpaceId: null,
                    buildingName: null,
                    lockIdentity: null,
                    spaceType: null,
                    spaceIdentity: null,
                    createdByPersonId: this.$store.getters.user.id
                  });
                }
                this.totalOtherToAdd = null;
              }
            },

            removeLock (index) {
              this.newFacilitiesLocks.splice(index, 1);
            }
        },
        created() {
            this.addLockFor = 0;
        },
        validations: {
            newFacilitiesLocks: {
              $each: {
                lockType: {
                  required
                },
                lockIdentity: {
                  required: requiredIf(function (value) {
                    return value.lockType == 0;
                  })
                }
              },
              mustBeUnique: function (values) {
                const newArrayOfObjects = values.reduce((accumulator, object) => {
                  const objectFound = accumulator.find(arrItem => arrItem.lockType === object.lockType && arrItem.buildingId === object.buildingId && arrItem.buildingSpaceId === object.buildingSpaceId && (arrItem.lockIdentity === null ? null : arrItem.lockIdentity.trim() === "" ? null : arrItem.lockIdentity) === (object.lockIdentity === null ? null : object.lockIdentity.trim() === "" ? null : object.lockIdentity));
                  if(objectFound) {
                      objectFound.times++;
                  } else {
                      object.times = 1;
                      accumulator.push(object);
                  }
                  return accumulator;
                }, []);
                return newArrayOfObjects.length === values.length;
              }
            }
        }
    }
</script>
